/*!

=========================================================
* Vision UI Free Chakra - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-chakra
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-chakra/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// Chakra imports
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Grid,
  Icon,
  Image,
  Progress,
  SimpleGrid,
  Spacer,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
// Styles for the circular progressbar
// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import BarChart from "components/Charts/BarChart";
import LineChart from "components/Charts/LineChart";
import IconBox from "components/Icons/IconBox";

// Icons
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
  RocketIcon,
  StatsIcon,
  WalletIcon,
} from "components/Icons/Icons.js";
import React, { useEffect, useState } from "react";

import { IoCheckmarkDoneCircleSharp } from "react-icons/io5";
// Data
import {
  barChartDataDashboard,
  barChartOptionsDashboard,
} from "variables/charts";
import useApiReq from "variables/apireq";
import { useLoader } from "components/Loader/loader";
import TablesTableRow from "components/Tables/TablesTableRow";
import ReactCustomMap from "components/Map/map";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { setIsLoading, isLoading } = useLoader();
  const [totalreaders, setChartValue] = useState([0]);
  const [lineChartLebels, setlineChartLebels] = useState([]);

  const [error, setError] = useState(null);
  const [barChartData, setBarChartData] = useState(barChartDataDashboard);
  const [barChartOptions, setBarChartOptions] = useState(
    barChartOptionsDashboard
  );
  const toast = useToast({ position: "top" });
  useEffect(() => {
    if (error) {
      toast({
        title: "An error occurred.",
        description: error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const chartLabel = data?.linechart.map((item) => item?.month) || [];
      const chartValue = [
        {
          name: "Readers",
          data: data?.linechart?.map((item) => item?.readers) || [],
        },
      ];
      const barChartData = [
        {
          name: "Total Page Views",
          data: data?.chart?.map((item) => item?.total_readers) || [],
        },
      ];
      const barChartOptions = data?.chart?.map((item) => item?.date) || [];
      setBarChartData(barChartData);
      setBarChartOptions(barChartOptions);
      setChartValue(chartValue);
      setlineChartLebels(chartLabel);
    }
  }, [data]);

  const fetchDashboardData = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const res = await useApiReq({
        url: "dashboard/",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (res.response) {
        setData(res.response);
      }
      if (res.error) {
        setError(res.error?.message || res.error);
        setData(null);
      }
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const text = (child) => {
    return (
      <Text fontSize="sm" color="#fff" fontWeight="normal">
        {child}
      </Text>
    );
  };
  const badge = (child) => {
    return (
      <Badge
        borderRadius="10px"
        px="2"
        colorScheme="teal"
        color="white"
        variant="solid"
        h={{ base: "20px", md: "30px" }}
        w={"auto"}
        fontSize={{ base: "sm", md: "md" }}
        textAlign="center"
        py={{ base: "4px", md: "4px" }}
      >
        {child}
      </Badge>
    );
  };

  const getOptimalData = (row, index) => {
    const data = {};
    data["Date"] = text(new Date(row.created_at).toLocaleDateString());
    data["Campaign Name"] = text(row.name);
    if (localStorage.getItem("group") !== "Advertiser") {
      data["Advertiser"] = text(row.package.advertiser_name);
    }
    data["Type"] = text(row.type);

    return data;
  };
  const getHeaders = () => {
    return [
      "Date",
      "Campaign Name",
      ...(localStorage.getItem("group") !== "Advertiser" ? ["Advertiser"] : []),
      "Type",
    ];
  };
  useEffect(() => {
    fetchDashboardData();
  }, []);
  if (isLoading) {
    return <div></div>;
  }
  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Grid
        templateColumns={{ sm: "1fr", lg: "55.6% 42.5%" }}
        maxW={{ sm: "100%", md: "100%" }}
        my="26px"
        gap="24px"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <Box>
          <SimpleGrid columns={{ sm: 1, md: 2, xl: 2 }} spacing="24px">
            {/* MiniStatistics Card */}
            <Card
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.15)"
              filter="blur(0px)"
              boxShadow="0px 7px 23px rgba(0, 0, 0, 0.05)"
              bg="linear-gradient(126.97deg, #060C29 28.26%, rgba(4, 12, 48, 0.5) 91.2%)"
              borderRadius="20px"
            >
              <CardBody>
                <Flex
                  flexDirection="row"
                  align="center"
                  justify="center"
                  w="100%"
                >
                  <Stat me="auto">
                    <StatLabel
                      fontSize="sm"
                      color="gray.400"
                      fontWeight="bold"
                      pb="2px"
                    >
                      Total PR
                    </StatLabel>
                    <Flex>
                      <StatNumber fontSize="lg" color="#fff">
                        {data?.total_pr}
                      </StatNumber>
                      <StatHelpText
                        alignSelf="flex-end"
                        justifySelf="flex-end"
                        m="0px"
                        color="green.400"
                        fontWeight="bold"
                        ps="3px"
                        fontSize="md"
                      ></StatHelpText>
                    </Flex>
                  </Stat>
                  <IconBox as="box" h={"45px"} w={"45px"} bg="brand.200">
                    <WalletIcon h={"24px"} w={"24px"} color="#fff" />
                  </IconBox>
                </Flex>
              </CardBody>
            </Card>
            {/* MiniStatistics Card */}
            <Card
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.15)"
              filter="blur(0px)"
              boxShadow="0px 7px 23px rgba(0, 0, 0, 0.05)"
              bg="linear-gradient(126.97deg, #060C29 28.26%, rgba(4, 12, 48, 0.5) 91.2%)"
              borderRadius="20px"
            >
              <CardBody>
                <Flex
                  flexDirection="row"
                  align="center"
                  justify="center"
                  w="100%"
                >
                  <Stat me="auto">
                    <StatLabel
                      fontSize="sm"
                      color="gray.400"
                      fontWeight="bold"
                      pb="2px"
                    >
                      {localStorage.getItem("group") === "Advertiser"
                        ? "Total Subscription"
                        : "Total Advertiser"}
                    </StatLabel>
                    <Flex>
                      <StatNumber fontSize="lg" color="#fff">
                        {localStorage.getItem("group") === "Advertiser"
                          ? data?.total_subscription
                          : data?.total_advertiser}
                      </StatNumber>
                      <StatHelpText
                        alignSelf="flex-end"
                        justifySelf="flex-end"
                        m="0px"
                        color="green.400"
                        fontWeight="bold"
                        ps="3px"
                        fontSize="md"
                      ></StatHelpText>
                    </Flex>
                  </Stat>
                  <IconBox as="box" h={"45px"} w={"45px"} bg="brand.200">
                    <GlobeIcon h={"24px"} w={"24px"} color="#fff" />
                  </IconBox>
                </Flex>
              </CardBody>
            </Card>
            {/* MiniStatistics Card */}
            <Card
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.15)"
              filter="blur(0px)"
              boxShadow="0px 7px 23px rgba(0, 0, 0, 0.05)"
              bg="linear-gradient(126.97deg, #060C29 28.26%, rgba(4, 12, 48, 0.5) 91.2%)"
              borderRadius="20px"
            >
              <CardBody>
                <Flex
                  flexDirection="row"
                  align="center"
                  justify="center"
                  w="100%"
                >
                  <Stat>
                    <StatLabel
                      fontSize="sm"
                      color="gray.400"
                      fontWeight="bold"
                      pb="2px"
                    >
                      {localStorage.getItem("group") === "Advertiser"
                        ? "Total Uniqe Readers"
                        : "Total Order"}
                    </StatLabel>
                    <Flex>
                      <StatNumber fontSize="lg" color="#fff">
                        {localStorage.getItem("group") === "Advertiser"
                          ? data?.total_uniqe_readers
                          : data?.total_order}
                      </StatNumber>
                      <StatHelpText
                        alignSelf="flex-end"
                        justifySelf="flex-end"
                        m="0px"
                        color="red.500"
                        fontWeight="bold"
                        ps="3px"
                        fontSize="md"
                      ></StatHelpText>
                    </Flex>
                  </Stat>
                  <Spacer />
                  <IconBox as="box" h={"45px"} w={"45px"} bg="brand.200">
                    <DocumentIcon h={"24px"} w={"24px"} color="#fff" />
                  </IconBox>
                </Flex>
              </CardBody>
            </Card>
            {/* MiniStatistics Card */}
            <Card
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.15)"
              filter="blur(0px)"
              boxShadow="0px 7px 23px rgba(0, 0, 0, 0.05)"
              bg="linear-gradient(126.97deg, #060C29 28.26%, rgba(4, 12, 48, 0.5) 91.2%)"
              borderRadius="20px"
            >
              <CardBody>
                <Flex
                  flexDirection="row"
                  align="center"
                  justify="center"
                  w="100%"
                >
                  <Stat me="auto">
                    <StatLabel
                      fontSize="sm"
                      color="gray.400"
                      fontWeight="bold"
                      pb="2px"
                    >
                      Total Page Views
                    </StatLabel>
                    <Flex>
                      <StatNumber fontSize="lg" color="#fff" fontWeight="bold">
                        {data?.total_pageview}
                      </StatNumber>
                      <StatHelpText
                        alignSelf="flex-end"
                        justifySelf="flex-end"
                        m="0px"
                        color="green.400"
                        fontWeight="bold"
                        ps="3px"
                        fontSize="md"
                      ></StatHelpText>
                    </Flex>
                  </Stat>
                  <IconBox as="box" h={"45px"} w={"45px"} bg="brand.200">
                    <CartIcon h={"24px"} w={"24px"} color="#fff" />
                  </IconBox>
                </Flex>
              </CardBody>
            </Card>
          </SimpleGrid>
          {/* On Going PR */}

          <Card p="28px 0px 0px 0px" mt="24px">
            <CardHeader mb="20px" ps="22px">
              <Flex direction="column" alignSelf="flex-start">
                <Text fontSize="lg" color="#fff" fontWeight="bold" mb="6px">
                  Month Wise Order
                </Text>
                <Text fontSize="md" fontWeight="medium" color="gray.400">
                  <Text as="span" color="green.400" fontWeight="bold"></Text>{" "}
                </Text>
              </Flex>
            </CardHeader>
            <Box w="100%" minH={{ sm: "300px" }}>
              <LineChart
                lineChartData={totalreaders}
                lineChartLebels={lineChartLebels}
                month={false}
              />
            </Box>
          </Card>
        </Box>
        {/* <Globe rotationSpeed={0.005} /> */}
        <ReactCustomMap locationList={data?.lat_lng || []} />
      </Grid>
      <Grid
        templateColumns={{ sm: "1fr", lg: "1.7fr 1.3fr" }}
        maxW={{ sm: "100%", md: "100%" }}
        gap="24px"
        mb="24px"
      >
        <Card p="16px" overflowX={{ sm: "scroll", xl: "auto" }}>
          <CardHeader p="12px 0px 28px 0px">
            <Flex direction="column">
              <Text fontSize="lg" color="#fff" fontWeight="bold" pb="8px">
                On Going PR
              </Text>
              <Flex align="center">
                <Icon
                  as={IoCheckmarkDoneCircleSharp}
                  color="teal.300"
                  w={4}
                  h={4}
                  pe="3px"
                  display={"none"}
                />
                <Text fontSize="sm" color="gray.400" fontWeight="normal">
                  <Text fontWeight="bold" as="span"></Text>{" "}
                </Text>
              </Flex>
            </Flex>
          </CardHeader>
          <Table
            variant="simple"
            style={{
              overflowX: "scroll",
            }}
            color="#fff"
          >
            <Thead>
              <Tr my=".8rem" ps="0px">
                {getHeaders().map((header) => {
                  return (
                    <Th
                      ps="0px"
                      color="gray.400"
                      fontFamily='"Oxygen",serif'
                      borderBottomColor="#56577A"
                      textAlign="center"
                    >
                      {header}
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {data?.campaigns?.reverse().map((row, index, arr) => {
                return (
                  <TablesTableRow
                    data={getOptimalData(row, index)}
                    lastItem={index === arr?.length - 1}
                    setOpen={() => {}}
                    isOpen={false}
                    openIndex={null}
                    currentIndex={index}
                  />
                );
              })}
            </Tbody>
          </Table>
        </Card>
        {/* 7 Days Report */}
        <Card p="16px">
          <CardBody>
            <Flex direction="column" w="100%">
              <Flex direction="column" mt="10px" alignSelf="flex-start">
                <Text fontSize="lg" color="#fff" fontWeight="bold" mb="6px">
                  7 Days Analytics
                </Text>
                <Text fontSize="md" fontWeight="medium" color="gray.400">
                  <Text as="span" color="green.400" fontWeight="bold"></Text>{" "}
                </Text>
              </Flex>
              <Box
                bg="linear-gradient(126.97deg, #060C29 28.26%, rgba(4, 12, 48, 0.5) 91.2%)"
                borderRadius="20px"
                display={{ sm: "flex", md: "block" }}
                justify={{ sm: "center", md: "flex-start" }}
                align={{ sm: "center", md: "flex-start" }}
                minH={{ sm: "180px", md: "270px" }}
                p={{ sm: "0px", md: "22px" }}
                mt="24px"
                mb="36px"
              >
                <BarChart
                  barChartOptions={barChartOptions}
                  barChartData={barChartData}
                />
              </Box>

              <SimpleGrid gap={{ sm: "12px" }} columns={4}>
                <Flex direction="column">
                  <Flex alignItems="center">
                    <IconBox
                      as="box"
                      h={"30px"}
                      w={"30px"}
                      bg="brand.200"
                      me="6px"
                    >
                      <WalletIcon h={"15px"} w={"15px"} color="#fff" />
                    </IconBox>
                    <Text fontSize=".7em" color="gray.400">
                      Readers
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ sm: "md", lg: "lg" }}
                    color="#fff"
                    fontWeight="bold"
                    mb="6px"
                    my="6px"
                  >
                    {data?.chart?.reduce((a, b) => a + b.total_readers, 0)}
                  </Text>
                  <Progress
                    colorScheme="brand"
                    bg="#2D2E5F"
                    borderRadius="30px"
                    h="5px"
                    value={20}
                  />
                </Flex>
                <Flex direction="column">
                  <Flex alignItems="center">
                    <IconBox
                      as="box"
                      h={"30px"}
                      w={"30px"}
                      bg="brand.200"
                      me="6px"
                    >
                      <RocketIcon h={"15px"} w={"15px"} color="#fff" />
                    </IconBox>
                    <Text fontSize=".7em" color="gray.400">
                      Spent Time
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ sm: "md", lg: "lg" }}
                    color="#fff"
                    fontWeight="bold"
                    mb="6px"
                    my="6px"
                  >
                    {(
                      data?.chart?.reduce(
                        (a, b) => a + b.total_reading_time,
                        0
                      ) / 60
                    ).toFixed(2)}
                    min
                  </Text>
                  <Progress
                    colorScheme="brand"
                    bg="#2D2E5F"
                    borderRadius="30px"
                    h="5px"
                    value={90}
                  />
                </Flex>
                <Flex direction="column">
                  <Flex alignItems="center">
                    <IconBox
                      as="box"
                      h={"30px"}
                      w={"30px"}
                      bg="brand.200"
                      me="6px"
                    >
                      <CartIcon h={"15px"} w={"15px"} color="#fff" />
                    </IconBox>
                    <Text fontSize=".7em" color="gray.400">
                      Avg. Spent Time
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ sm: "md", lg: "lg" }}
                    color="#fff"
                    fontWeight="bold"
                    mb="6px"
                    my="6px"
                  >
                    {(
                      data?.chart?.reduce((a, b) => a + b.avg_reading_time, 0) /
                      60
                    ).toFixed(2)}{" "}
                    min
                  </Text>
                  <Progress
                    colorScheme="brand"
                    bg="#2D2E5F"
                    borderRadius="30px"
                    h="5px"
                    value={30}
                  />
                </Flex>
                <Flex direction="column">
                  <Flex alignItems="center">
                    <IconBox
                      as="box"
                      h={"30px"}
                      w={"30px"}
                      bg="brand.200"
                      me="6px"
                    >
                      <StatsIcon h={"15px"} w={"15px"} color="#fff" />
                    </IconBox>
                    <Text fontSize=".7em" color="gray.400">
                      Unique Readers
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ sm: "md", lg: "lg" }}
                    color="#fff"
                    fontWeight="bold"
                    mb="6px"
                    my="6px"
                  >
                    {data?.total_unique_readers_7days || 0}
                  </Text>
                  <Progress
                    colorScheme="brand"
                    bg="#2D2E5F"
                    borderRadius="30px"
                    h="5px"
                    value={50}
                  />
                </Flex>
              </SimpleGrid>
            </Flex>
          </CardBody>
        </Card>
        {/* Month wise page view */}
      </Grid>
    </Flex>
  );
}
